/**
 * Delivery & Installation Estimate Renderer
 * ------------------------------------------------------------
 * Reads the estimate data that Liquid serialized into the section from the
 * `delivery_estimate` metaobject (shop metafield custom.delivery_estimates), for the
 * customer's detected country only (resolved server-side from the global country selector),
 * then renders:
 *   - Curbside delivery estimate                         (delivery column)
 *   - Delivered to final location estimate               (installation column)
 *   - Final location + bolting estimate (base + bolting) (installation column)
 *
 * There is no in-section country switcher and no bolting control: both installation lines
 * are always shown as read-only estimates. Weight is read live from the DOM so the same
 * render path serves first paint and variant changes (via delivery-info-updater.js).
 * All prices are display-only estimates; final pricing is confirmed after the delivery form.
 */
class DeliveryPricingRenderer {
  constructor() {
    this.sections = document.querySelectorAll('.installation-delivery-section[data-enable-dynamic="true"]');
    this.sections.forEach((section) => this.renderSection(section));
  }

  parseData(sectionEl) {
    const raw = sectionEl.dataset.pricingJson;
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      console.error('[DeliveryEstimate] Invalid pricing JSON:', e);
      return null;
    }
  }

  getWeightKg(sectionEl) {
    return parseFloat(sectionEl.dataset.weightKg) || 0;
  }

  // Map product weight (kg) to the matching weight-band index; heaviest band as fallback.
  // Weight is rounded to the nearest kg so fractional weights that fall in the gaps
  // between integer band bounds (e.g. 20.5 between [0-20] and [21-100]) resolve to the
  // correct adjacent band instead of dropping through to the heaviest-band fallback.
  weightBandIndex(weightKg, bands) {
    const w = Math.round(weightKg);
    for (let i = 0; i < bands.length; i++) {
      const min = bands[i][0];
      const max = bands[i][1];
      if (w >= min && w <= max) return i;
    }
    return bands.length - 1;
  }

  renderSection(sectionEl) {
    const data = this.parseData(sectionEl);
    if (!data || !Array.isArray(data.curbside) || !Array.isArray(data.final_location) || data.curbside.length === 0) {
      return this.showError(sectionEl);
    }

    const bands = Array.isArray(data.bands) && data.bands.length ? data.bands : [[0, 999999]];
    const idx = this.weightBandIndex(this.getWeightKg(sectionEl), bands);
    const currency = data.currency || 'EUR';
    const locale = sectionEl.dataset.locale || 'en';
    const approx = sectionEl.dataset.approxText || 'Approx.';
    const labels = data.labels || {};

    const fmt = (cents) => this.formatMoney(cents, currency, locale);
    const priceAt = (arr) => (Array.isArray(arr) ? (Number(arr[idx]) || 0) : 0);

    // Delivery column - curbside estimate (single row; no warehouse option)
    const deliveryEl = sectionEl.querySelector('[data-pricing-type="delivery"]');
    if (deliveryEl) {
      deliveryEl.innerHTML = this.deliveryRow(
        labels.curbside || 'Curbside Delivery',
        labels.curbsideDescription || '',
        priceAt(data.curbside),
        approx,
        fmt
      );
    }

    // Installation column - always show BOTH lines (informational, non-selectable):
    //   1) delivered to final location              = base
    //   2) final location with bolting to floor/wall = base + bolting
    const installEl = sectionEl.querySelector('[data-pricing-type="installation"]');
    if (installEl) {
      const base = priceAt(data.final_location);
      const bolt = priceAt(data.bolting);
      installEl.innerHTML =
        this.pricingRow(labels.final_location || 'Delivered to final location', base, approx, fmt) +
        this.pricingRow(
          labels.bolting_combo || 'Delivered to final location with bolting to floor/wall',
          base + bolt,
          approx,
          fmt
        );
    }
  }

  deliveryRow(label, description, cents, approx, fmt) {
    return (
      '<div class="delivery-option" data-option-id="curbside">' +
      '<div class="delivery-option-header">' +
      '<span class="delivery-label">' + this.escape(label) + '</span>' +
      '<span class="delivery-price" data-price-cents="' + cents + '">' +
      '<span class="approx-text">' + this.escape(approx) + '</span> ' +
      '<span class="price-amount">' + fmt(cents) + '</span>' +
      '</span>' +
      '</div>' +
      (description ? '<p class="delivery-description">' + this.escape(description) + '</p>' : '') +
      '</div>'
    );
  }

  pricingRow(label, cents, approx, fmt) {
    return (
      '<div class="pricing-row">' +
      '<span class="pricing-label">' + this.escape(label) + '</span>' +
      '<span class="pricing-value" data-price-cents="' + cents + '">' +
      '<span class="approx-text">' + this.escape(approx) + '</span> ' +
      '<span class="price-amount">' + fmt(cents) + '</span>' +
      '</span>' +
      '</div>'
    );
  }

  // NO DECIMALS. Safegear 2026-09-12: every figure this section prints is an ESTIMATE,
  // and "Ca. 4.200,00 kr." claimed a precision the number does not have. One change
  // here covers all three rows, because deliveryRow and pricingRow share this fmt.
  //
  // BOTH minimumFractionDigits AND maximumFractionDigits have to be set. The
  // 'currency' style defaults the MINIMUM to 2, so setting only the maximum to 0
  // leaves min > max, Intl throws a RangeError, and every price on the page drops
  // through to the bare `currency + amount` fallback at the bottom. Do not remove
  // either line.
  formatMoney(cents, currency, locale) {
    const amount = (Number(cents) || 0) / 100;
    const opts = {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    };
    try {
      return new Intl.NumberFormat(locale, opts).format(amount);
    } catch (e) {
      try {
        return new Intl.NumberFormat('en', opts).format(amount);
      } catch (e2) {
        return currency + ' ' + amount.toFixed(0);
      }
    }
  }

  escape(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  showError(sectionEl) {
    const label = sectionEl.dataset.pricingErrorText || 'Estimate unavailable';
    const html = '<div class="pricing-error">' + this.escape(label) + '</div>';
    const d = sectionEl.querySelector('[data-pricing-type="delivery"]');
    const i = sectionEl.querySelector('[data-pricing-type="installation"]');
    if (d) d.innerHTML = html;
    if (i) i.innerHTML = html;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () {
    new DeliveryPricingRenderer();
  });
} else {
  new DeliveryPricingRenderer();
}

// Exposed so the variant-change updater can re-render after weight changes.
window.DeliveryPricingRenderer = DeliveryPricingRenderer;
